import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { normalizeGTINForStorage, toStoredConsumerEan } from '../libs/gtinUtils.js';
import GTINInput from './GTINInput.jsx';
import UnitInput from './UnitInput.jsx';
import { primaryButtonClasses } from '../libs/sharedClassNames.js';
import { createProductAndCasepack } from '../libs/fetch.js';

// Reusable Product Line Dropdown Component
const ProductLineDropdown = ({ 
  value, 
  onChange, 
  existingProductLines: productLineOptions = [], 
  placeholder = "Select or add product line...",
  className = ""
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  const getFilteredOptions = () => {
    const searchTermLower = searchTerm.toLowerCase();
    const options = [];
    
    // Add "none" option if there's no search term
    if (!searchTerm) {
      options.push({ value: null, isNew: false, display: '(none)' });
    }
    
    // Add existing product lines that match search
    productLineOptions.forEach(line => {
      if (line.toLowerCase().includes(searchTermLower)) {
        options.push({ value: line, isNew: false });
      }
    });
    
    // Add "add new" option if search has value and no exact match
    if (searchTerm && !productLineOptions.some(line => line.toLowerCase() === searchTermLower)) {
      options.push({ value: searchTerm, isNew: true });
    }
    
    // If no options and no search term, show all existing product lines
    if (options.length === 1 && !searchTerm) { // Only "none" option
      productLineOptions.forEach(line => {
        options.push({ value: line, isNew: false });
      });
    }
    
    return options;
  };

  const handleSelect = (option) => {
    if (option.isNew) {
      onChange(option.value);
    } else {
      onChange(option.value);
    }
    setShowDropdown(false);
    setSearchTerm('');
    setSelectedOptionIndex(0);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setSearchTerm('');
      setSelectedOptionIndex(0);
    }
  };

  const handleKeyDown = (e) => {
    const filteredOptions = getFilteredOptions();
    
    if (filteredOptions.length === 0) return;
    
    let currentIndex = selectedOptionIndex;
    
    // Ensure index is within bounds
    if (currentIndex < 0) currentIndex = 0;
    if (currentIndex >= filteredOptions.length) currentIndex = filteredOptions.length - 1;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        if (currentIndex < filteredOptions.length - 1) {
          setSelectedOptionIndex(currentIndex + 1);
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        if (currentIndex > 0) {
          setSelectedOptionIndex(currentIndex - 1);
        }
        break;
        
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (currentIndex >= 0 && currentIndex < filteredOptions.length) {
          const selectedOption = filteredOptions[currentIndex];
          handleSelect(selectedOption);
        } else if (searchTerm.trim()) {
          // If no option is selected but there's a search term, create new
          const newOption = { value: searchTerm.trim(), isNew: true };
          handleSelect(newOption);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setShowDropdown(false);
        setSearchTerm('');
        setSelectedOptionIndex(0);
        break;
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.product-line-dropdown')) {
        setShowDropdown(false);
        setSearchTerm('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white"
      >
        {value || placeholder}
        <ChevronDownIcon className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 transition-transform ${
          showDropdown ? 'rotate-180' : ''
        }`} />
      </button>
      
      {showDropdown && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 text-sm max-h-60 overflow-y-auto product-line-dropdown">
          {/* Search input */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedOptionIndex(0); // Reset selection when search changes
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search or add..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoFocus
            />
          </div>
          
          {/* Filtered existing product lines */}
          {getFilteredOptions().map((option, index) => {
            const isSelected = index === selectedOptionIndex;
            return (
              <button
                key={option.value || 'none'}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full px-3 py-2 text-left text-gray-900 block ${
                  isSelected
                    ? 'bg-indigo-50 text-indigo-900' 
                    : 'hover:bg-gray-50'
                } ${option.isNew ? 'border-t border-gray-200 bg-green-50 hover:bg-green-100' : ''}`}
              >
                {option.isNew ? (
                  <span className="text-green-700">+ Add "{option.value}"</span>
                ) : (
                  option.display || option.value
                )}
              </button>
            );
          })}
          
          {/* No results message */}
          {searchTerm && getFilteredOptions().length === 0 && (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No matching product lines found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ConsumerProductForm = ({ onSubmit, onCancel, product = null, existingProducts = [], existingProductLines = [], organizationId }) => {
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [formData, setFormData] = useState({
    name: product?.name || '',
    product_line: product?.product_line || '',
    ean_upc: product?.ean_upc || '',
    customer_sku: product?.customer_sku || '',
    is_seasonal: product?.is_seasonal || false,
    is_discontinued: product?.is_discontinued || false,
    default_casepack_units: product?.default_casepack_units || '',
    default_casepack_name: product?.default_casepack_name || '',
    default_casepack_gtin: product?.default_casepack_gtin || ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isCasepackNameAutoGenerated, setIsCasepackNameAutoGenerated] = useState(true);

  // Store original form data for comparison
  const originalFormData = {
    name: product?.name || '',
    product_line: product?.product_line || '',
    ean_upc: product?.ean_upc || '',
    customer_sku: product?.customer_sku || '',
    is_seasonal: product?.is_seasonal || false,
    is_discontinued: product?.is_discontinued || false,
    default_casepack_units: product?.default_casepack_units || '',
    default_casepack_name: product?.default_casepack_name || '',
    default_casepack_gtin: product?.default_casepack_gtin || ''
  };

  // Check if form has changes compared to original data
  useEffect(() => {
    const hasFormChanges = Object.keys(formData).some(key => {
      const originalValue = originalFormData[key];
      const currentValue = formData[key];
      
      // Handle different data types appropriately
      if (typeof originalValue === 'number' && typeof currentValue === 'number') {
        return originalValue !== currentValue;
      }
      
      // For strings, trim whitespace for comparison
      if (typeof originalValue === 'string' && typeof currentValue === 'string') {
        return (originalValue || '').trim() !== (currentValue || '').trim();
      }
      
      return originalValue !== currentValue;
    });
    
    setHasChanges(hasFormChanges);
  }, [formData, originalFormData]);

  // Handle escape key to close form
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [hasChanges]);

  // Handle click outside to close form
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.classList.contains('bg-gray-500')) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hasChanges]);

  // Auto-generate casepack name when product name or units change
  useEffect(() => {
    if (isCasepackNameAutoGenerated) {
      if (formData.name && formData.name.trim() && formData.default_casepack_units && formData.default_casepack_units.toString().trim()) {
        const generatedName = `${formData.name} (Case of ${formData.default_casepack_units})`;
        setFormData(prev => ({
          ...prev,
          default_casepack_name: generatedName
        }));
      } else {
        // Clear the casepack name when product name or units are missing
        setFormData(prev => ({
          ...prev,
          default_casepack_name: ''
        }));
      }
    }
  }, [formData.name, formData.default_casepack_units, isCasepackNameAutoGenerated]);

  const getUnitsWarning = () => {
    const units = formData.default_casepack_units;
    const unitsNum = typeof units === 'string' ? parseInt(units) || 0 : (units || 0);
    if (unitsNum > 999) {
      return 'Large quantity detected. Please double-check this value.';
    }
    return null;
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (formData.name.length > 255) {
      newErrors.name = 'Product name must be less than 255 characters';
    }
    
    if (formData.product_line && formData.product_line.length > 100) {
      newErrors.product_line = 'Product line must be less than 100 characters';
    }
    
    // Units per casepack validation
    if (!formData.default_casepack_units || formData.default_casepack_units < 1) {
      newErrors.default_casepack_units = 'Units per casepack must be at least 1';
    }

    // Casepack name is required when not auto-generated
    if (!isCasepackNameAutoGenerated && !formData.default_casepack_name.trim()) {
      newErrors.default_casepack_name = 'Casepack name is required when not auto-generated';
    }

    // Check that product GTIN and casepack GTIN don't conflict with each other
    if (formData.ean_upc && formData.default_casepack_gtin) {
      const normalizedProductGtin = normalizeGTINForStorage(formData.ean_upc);
      const normalizedCasepackGtin = normalizeGTINForStorage(formData.default_casepack_gtin);
      
      if (normalizedProductGtin === normalizedCasepackGtin) {
        newErrors.default_casepack_gtin = 'Casepack GTIN cannot be the same as the product GTIN';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isCasepackNameAutoGenerated]);

  // Run validation whenever form data or auto-generation state changes
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Check if form is valid and has changes
  const isFormValid = () => {
    return hasChanges && Object.keys(errors).every(key => !errors[key]);
  };

  // Handle cancel with unsaved changes warning
  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setShowCancelWarning(true);
    } else {
      onCancel();
    }
  }, [hasChanges, onCancel]);

  // Confirm cancel action
  const confirmCancel = () => {
    setShowCancelWarning(false);
    onCancel();
  };

  // Reset form to original state
  const resetForm = () => {
    setFormData(originalFormData);
    setErrors({});
    setHasChanges(false);
    setIsCasepackNameAutoGenerated(true);
  };

  // Get unique product lines from existing products
  const productLineOptions = useMemo(() => {
    return existingProductLines;
  }, [existingProductLines]);

  const handleProductLineSelect = (value) => {
    handleInputChange('product_line', value);
  };

  const handleCasepackNameAutoGeneratedChange = (checked) => {
    setIsCasepackNameAutoGenerated(checked);
    if (checked) {
      // Auto-generate the name if we have the required data
      if (formData.name && formData.name.trim() && formData.default_casepack_units) {
        const generatedName = `${formData.name} (Case of ${formData.default_casepack_units})`;
        setFormData(prev => ({
          ...prev,
          default_casepack_name: generatedName
        }));
      } else {
        // Clear the name when auto-generation is enabled but data is missing
        setFormData(prev => ({
          ...prev,
          default_casepack_name: ''
        }));
      }
    } else {
      // Clear the name so user can enter manually
      setFormData(prev => ({
        ...prev,
        default_casepack_name: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data
      const productData = {
        ...formData,
        ean_upc: toStoredConsumerEan(formData.ean_upc),
        default_casepack_gtin: normalizeGTINForStorage(formData.default_casepack_gtin)
      };

      // Convert empty strings to null for optional fields
      if (productData.product_line === '') {
        productData.product_line = null;
      }
      if (productData.ean_upc === '') {
        productData.ean_upc = null;
      }
      if (productData.customer_sku === '') {
        productData.customer_sku = null;
      }
      if (productData.default_casepack_gtin === '') {
        productData.default_casepack_gtin = null;
      }
      
      // Handle empty units - convert to null for database
      if (productData.default_casepack_units === '') {
        productData.default_casepack_units = null;
      }

      // If casepack name is auto-generated, save as null
      if (isCasepackNameAutoGenerated) {
        productData.default_casepack_name = null;
      }
      
      // Call the new API function
      const result = await createProductAndCasepack(organizationId, productData);
      
      if (result.error) {
        console.error('Error creating product:', result.error);
        // You might want to show an error message to the user here
        return;
      }
      
      // Call the onSubmit callback with the result
      if (onSubmit) {
        onSubmit(result.data);
      }
      
      // Close the modal
      onCancel();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium text-gray-900">
                {product ? 'Edit Consumer Product' : 'New Consumer Product'}
              </h3>
              {hasChanges && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Unsaved Changes
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4" autoComplete="off">
          <div className="space-y-6">
            {/* Product Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Product Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`mt-1 block w-full h-8 px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 sm:text-sm ${
                  errors.name ? 'border-red-300' : ''
                }`}
                placeholder="Enter product name"
                autoComplete="off"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Product Line */}
            <div>
              <label htmlFor="product_line" className="block text-sm font-medium text-gray-700">
                Product Line (Optional)
              </label>
              <ProductLineDropdown
                value={formData.product_line}
                onChange={handleProductLineSelect}
                existingProductLines={productLineOptions}
                placeholder="Select or add product line..."
                className="mt-1"
              />
              {errors.product_line && (
                <p className="mt-1 text-sm text-red-600">{errors.product_line}</p>
              )}
            </div>

            {/* Product Identifier */}
            <div>
              <label htmlFor="ean_upc" className="block text-sm font-medium text-gray-700">
                UPC/EAN (Optional)
              </label>
              <GTINInput
                value={formData.ean_upc}
                onChange={(value) => handleInputChange('ean_upc', value)}
                existingProducts={existingProducts}
                excludeProductId={product?.id}
                originalGtin={product?.ean_upc}
                maxLength={13}
                placeholder="Enter 12-digit UPC or 13-digit EAN"
                className="mt-1"
              />
            </div>

            {/* SKU and Product Flags */}
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <label htmlFor="customer_sku" className="block text-sm font-medium text-gray-700">
                  SKU (Optional)
                </label>
                <input
                  type="text"
                  id="customer_sku"
                  value={formData.customer_sku}
                  onChange={(e) => handleInputChange('customer_sku', e.target.value)}
                  className="mt-1 block w-full h-8 px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter SKU"
                  autoComplete="off"
                />
              </div>
              <div className="flex flex-col gap-2 items-start">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_seasonal}
                    onChange={(e) => handleInputChange('is_seasonal', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Seasonal</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_discontinued}
                    onChange={(e) => handleInputChange('is_discontinued', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Discontinued</span>
                </label>
              </div>
            </div>

            {/* Default Casepack Section */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Default Casepack</h4>
              
              {/* Units per Casepack */}
              <div>
                <label htmlFor="default_casepack_units" className="block text-sm font-medium text-gray-700">
                  Units per Casepack *
                </label>
                <UnitInput
                  value={formData.default_casepack_units}
                  onChange={(value) => handleInputChange('default_casepack_units', value)}
                  min="1"
                  className={`mt-1 block w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                    errors.default_casepack_units ? 'border-red-300' : ''
                  }`}
                />
                {errors.default_casepack_units && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_casepack_units}</p>
                )}
                {!errors.default_casepack_units && getUnitsWarning() && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="relative group">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                        {getUnitsWarning()}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Casepack Name */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="default_casepack_name" className="block text-sm font-medium text-gray-700">
                    Casepack Name
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="auto_generate_casepack_name"
                      checked={isCasepackNameAutoGenerated}
                      onChange={(e) => handleCasepackNameAutoGeneratedChange(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="auto_generate_casepack_name" className="ml-2 text-sm text-gray-600">
                      Auto-generate
                    </label>
                  </div>
                </div>
                <input
                  type="text"
                  id="default_casepack_name"
                  value={formData.default_casepack_name}
                  onChange={(e) => handleInputChange('default_casepack_name', e.target.value)}
                  disabled={isCasepackNameAutoGenerated}
                  required={!isCasepackNameAutoGenerated}
                  className={`mt-1 block w-full h-8 px-3 py-2 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-indigo-500 sm:text-sm ${
                    isCasepackNameAutoGenerated ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'
                  }`}
                  placeholder={isCasepackNameAutoGenerated 
                    ? (formData.name && formData.name.trim() && formData.default_casepack_units ? `${formData.name} (Case of ${formData.default_casepack_units})` : "Auto-generated name")
                    : "Enter custom casepack name"
                  }
                  autoComplete="off"
                />
                {errors.default_casepack_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_casepack_name}</p>
                )}
              </div>

              {/* Casepack GTIN/UPC */}
              <div className="mt-4">
                <label htmlFor="default_casepack_gtin" className="block text-sm font-medium text-gray-700">
                  Casepack GTIN/UPC (Optional)
                </label>
                <GTINInput
                  value={formData.default_casepack_gtin}
                  onChange={(value) => handleInputChange('default_casepack_gtin', value)}
                  existingProducts={existingProducts}
                  excludeProductId={product?.id}
                  originalGtin={product?.default_casepack_gtin}
                  maxLength={14}
                  placeholder="Enter 12-digit UPC or 14-digit GTIN"
                  className="mt-1"
                />
                {errors.default_casepack_gtin && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_casepack_gtin}</p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-8 flex justify-end space-x-3">
            {hasChanges && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Reset
              </button>
            )}
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
              className={`px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                isSubmitting || !isFormValid() 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : primaryButtonClasses
              }`}
            >
              {isSubmitting 
                ? (product ? 'Updating...' : 'Creating...')
                : !hasChanges 
                  ? (product ? 'No Changes' : 'Create')
                  : Object.keys(errors).some(key => errors[key])
                    ? 'Fix Errors'
                    : (product ? 'Update Product' : 'Create Product')
              }
            </button>
          </div>

        </form>
      </div>

      {/* Cancel Warning Modal */}
      {showCancelWarning && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                You have unsaved changes. Are you sure you want to cancel? All changes will be lost.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCancelWarning(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Continue Editing
                </button>
                <button
                  type="button"
                  onClick={confirmCancel}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumerProductForm;
